import React, { useState, useEffect } from 'react';
import './Hospital_DoctorDashboard.css';
import VideoCallModal from '../components/VideoCallModal';
import { createWebSocketConnection } from '../utils/webrtcConfig';
import {
  doctorListSessions,
  doctorListSessionNotes,
  doctorListAppointments,
  doctorDeleteAppointment,
  hospitalGetDoctorPatients,
} from '../components/api';
import useTranslation from '../hooks/useTranslation';
import useTheme from '../hooks/useTheme';
import { formatDate, formatDateTime, getAssessmentBadgeClass } from '../utils/dateFormatting';
import { authFetch, authHeaders } from '../utils/authFetch';
import SessionPanel from '../components/hospitalDoctor/SessionPanel';
import PatientListPanel from '../components/hospitalDoctor/PatientListPanel';
import ReportView from '../components/hospitalDoctor/ReportView';

const API_HOST = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:8000' : 'https://app.neurolonic.com/api');

export default function Hospital_DoctorDashboard() {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('patients');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctorId, setDoctorId] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionPatient, setSessionPatient] = useState(null);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [newAppointmentIds, setNewAppointmentIds] = useState(new Set());
  const [sessionHistory, setSessionHistory] = useState([]);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [selectedSessionDetails, setSelectedSessionDetails] = useState(null);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportPatient, setReportPatient] = useState(null);
  const [lastSessionNote, setLastSessionNote] = useState('');
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [showDeleteAppointmentModal, setShowDeleteAppointmentModal] = useState(false);
  const [selectedAppointmentForDelete, setSelectedAppointmentForDelete] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const id = localStorage.getItem('neuro_user_id');
    setDoctorId(id);
    if (id) {
      authFetch(`${API_HOST}/user/details?user_id=${id}`, { headers: authHeaders() })
        .then(res => res.json())
        .then(data => {
          if (data.status_code === 200 && data.data) {
            try {
              const info = typeof data.data.info === 'string' ? JSON.parse(data.data.info) : data.data.info;
              if (info && info.hospital_id) {
                loadPatients(info.hospital_id, id);
                loadAppointments(id);
              } else {
                console.error('Hospital doctor missing hospital_id in info field');
              }
            } catch (e) {
              console.error('Error parsing user info:', e);
            }
          }
        })
        .catch(err => {
          console.error('Error fetching user details:', err);
        });
    }
  }, []);

  useEffect(() => {
    if (!doctorId) return;
    let userWs = null;
    const connectWebSocket = async () => {
      try {
        userWs = await createWebSocketConnection(`user/${doctorId}`);
        userWs.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'new_appointment') {
              setNewAppointmentIds(prev => new Set([...prev, data.appointment_id]));
              loadAppointments(doctorId);
              if (Notification.permission === 'granted') {
                new Notification('New Appointment Scheduled', {
                  body: `Appointment with ${data.patient_name} on ${data.date} at ${data.time}`,
                  icon: '/favicon.ico'
                });
              }
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };
      } catch (e) {
        console.error('WebSocket connection error:', e);
      }
    };
    connectWebSocket();
    return () => {
      if (userWs) {
        try { userWs.close(); } catch {}
      }
    };
  }, [doctorId]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(prev => prev + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadPatients = async (hospId, docId) => {
    setLoading(true);
    try {
      const res = await hospitalGetDoctorPatients(hospId, docId);
      if (res.status_code === 200) {
        setPatients(res.data || []);
      }
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSessionHistory = async (patientId) => {
    try {
      const res = await doctorListSessions(patientId);
      if (res.status_code === 200) {
        setSessionHistory(res.data || []);
        return res.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error loading session history:', error);
      return [];
    }
  };

  const loadSessionNotes = async (sessionId) => {
    try {
      const res = await doctorListSessionNotes(sessionId);
      if (res.status_code === 200) {
        setSessionNotes(res.data || []);
      }
    } catch (error) {
      console.error('Error loading session notes:', error);
    }
  };

  const loadAppointments = async (id) => {
    try {
      const res = await doctorListAppointments(id);
      if (res.status_code === 200) {
        setAppointments(res.data || []);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const handleStartSession = (patient) => {
    setSessionPatient(patient);
    setShowSessionModal(true);
  };

  const formatDateFallback = (dateStr) => formatDate(dateStr) || 'Not scheduled';
  const formatDateTimeFallback = (dateStr, timeStr) => formatDateTime(dateStr, timeStr) || 'Not scheduled';

  const isAppointmentUpcoming = (dateStr, timeStr) => {
    if (!dateStr) return false;
    try {
      const [hours, minutes] = timeStr ? timeStr.split(':') : ['00', '00'];
      const appointmentDate = new Date(dateStr);
      appointmentDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      const now = new Date();
      return appointmentDate >= now;
    } catch {
      return false;
    }
  };

  const canStartSession = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return false;
    try {
      const [hours, minutes] = timeStr.split(':');
      const appointmentDate = new Date(dateStr);
      appointmentDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      const now = new Date();
      const startWindow = new Date(appointmentDate.getTime() - 15 * 60 * 1000);
      const endWindow = new Date(appointmentDate.getTime() + 2 * 60 * 60 * 1000);
      return now >= startWindow && now <= endWindow;
    } catch {
      return false;
    }
  };

  const handleViewReport = async (patient) => {
    setReportPatient(patient);
    const history = await loadSessionHistory(patient.id);
    if (history && history.length > 0) {
      try {
        const notesRes = await doctorListSessionNotes(history[0].session_id);
        if (notesRes && notesRes.status_code === 200 && Array.isArray(notesRes.data) && notesRes.data.length > 0) {
          setLastSessionNote(notesRes.data[0].note_text || '');
        } else {
          setLastSessionNote('');
        }
      } catch (error) {
        console.error('Error loading session notes:', error);
        setLastSessionNote('');
      }
    } else {
      setLastSessionNote('');
    }
    setShowReportModal(true);
  };

  const handleViewHistory = async (patient) => {
    setSelectedPatient(patient);
    await loadSessionHistory(patient.id);
    setShowSessionHistory(true);
  };

  return (
    <div className="hospital-doctor-dashboard">
      <div className="hospital-doctor-header">
        <div className="hospital-doctor-header-row">
          <h1>{t('Hospital Doctor Dashboard')}</h1>
          <button
            aria-label={t('Toggle theme')}
            className="hospital-theme-toggle"
            onClick={toggleTheme}
            title={`${t('Theme')}: ${theme.charAt(0).toUpperCase() + theme.slice(1)}`}
          >
            {theme === "light" && <span role="img" aria-label={t('Light')}>🌞</span>}
            {theme === "dark" && <span role="img" aria-label={t('Dark')}>🌜</span>}
            {theme === "device" && <span role="img" aria-label={t('Device')}>💻</span>}
          </button>
        </div>
        <div className="hospital-doctor-tabs">
          <button
            className={activeTab === 'patients' ? 'active' : ''}
            onClick={() => setActiveTab('patients')}
          >
            {t('My Patients')}
          </button>
          <button
            className={activeTab === 'appointments' ? 'active' : ''}
            onClick={() => setActiveTab('appointments')}
          >
            {t('Scheduled Sessions')}
            {newAppointmentIds.size > 0 && (
              <span className="notification-badge">{newAppointmentIds.size}</span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'patients' && (
        <div className="hospital-doctor-section">
          <h2>{t('Assigned Patients')}</h2>
          <PatientListPanel
            patients={patients}
            loading={loading}
            searchQuery={patientSearchQuery}
            onSearchChange={setPatientSearchQuery}
            onStartSession={handleStartSession}
            onViewReport={handleViewReport}
            onViewHistory={handleViewHistory}
            refreshTrigger={refreshTrigger}
          />
        </div>
      )}

      {activeTab === 'appointments' && (
        <div className="hospital-doctor-section">
          <h2>{t('Scheduled Sessions')}</h2>
          {(() => {
            const validAppointments = appointments.filter(apt => {
              return patients.some(p => p.id === apt.patient_id);
            });

            return validAppointments.length === 0 ? (
              <div className="empty-state">{t('No scheduled sessions.')}</div>
            ) : (
              <>
                <div className="appointments-summary">
                  <strong>{t('Upcoming Appointments')}:</strong> {validAppointments.filter(a => isAppointmentUpcoming(a.date, a.time)).length}
                </div>
                <table className="appointments-table">
                  <thead>
                    <tr>
                      <th>{t('Patient')}</th>
                      <th>{t('Date & Time')}</th>
                      <th>{t('Purpose')}</th>
                      <th>{t('Notes')}</th>
                      <th>{t('Duration')}</th>
                      <th>{t('Status')}</th>
                      <th>{t('Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validAppointments
                      .sort((a, b) => {
                        const dateA = a.date ? new Date(a.date + ' ' + (a.time || '00:00')) : new Date(0);
                        const dateB = b.date ? new Date(b.date + ' ' + (b.time || '00:00')) : new Date(0);
                        return dateA - dateB;
                      })
                      .map((apt) => {
                      const isUpcoming = isAppointmentUpcoming(apt.date, apt.time);
                      const canStart = canStartSession(apt.date, apt.time);
                      const patient = patients.find(p => p.id === apt.patient_id);
                      return (
                        <tr
                          key={apt.id}
                          className={`${newAppointmentIds.has(String(apt.id)) ? 'new-appointment' : ''} ${isUpcoming ? (canStart ? 'appointment-ready' : 'appointment-upcoming') : 'appointment-past'}`}
                        >
                          <td>{apt.patient_name || 'Unknown'}</td>
                          <td>{formatDateTimeFallback(apt.date, apt.time)}</td>
                          <td>{apt.purpose || '-'}</td>
                          <td className="notes-cell">{apt.notes || '-'}</td>
                          <td>{apt.duration || apt.duration_minutes || 30} min</td>
                          <td>
                            {isUpcoming ? (
                              canStart ? (
                                <span className="status-ready">Ready to Start</span>
                              ) : (
                                <span className="status-upcoming">Upcoming</span>
                              )
                            ) : (
                              <span className="status-past">Past</span>
                            )}
                          </td>
                          <td>
                            {patient ? (
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {canStart && (
                                  <button
                                    className="btn-primary"
                                    onClick={() => {
                                      handleStartSession(patient);
                                      setNewAppointmentIds(prev => {
                                        const next = new Set(prev);
                                        next.delete(String(apt.id));
                                        return next;
                                      });
                                    }}
                                  >
                                    Start Session
                                  </button>
                                )}
                                <button
                                  className="btn-secondary"
                                  onClick={() => handleViewReport(patient)}
                                  style={{
                                    background: '#ffffff',
                                    color: '#000000',
                                    border: '1px solid #d1d5db',
                                    fontWeight: '600'
                                  }}
                                >
                                  Report
                                </button>
                                {!isUpcoming && (
                                  <button
                                    className="btn-secondary"
                                    onClick={() => {
                                      setSelectedAppointmentForDelete(apt);
                                      setShowDeleteAppointmentModal(true);
                                    }}
                                    style={{
                                      fontSize: '12px',
                                      padding: '6px 12px',
                                      background: '#ef4444',
                                      color: '#ffffff',
                                      border: '1px solid #ef4444'
                                    }}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted">Patient not found</span>
                            )}
                          </td>
                        </tr>
                      );
                      })}
                  </tbody>
                </table>
              </>
            );
          })()}
        </div>
      )}

      <SessionPanel
        patient={sessionPatient}
        isOpen={showSessionModal}
        onClose={() => setShowSessionModal(false)}
        onSessionSaved={() => {
          const id = localStorage.getItem('neuro_user_id');
          if (id) {
            authFetch(`${API_HOST}/user/details?user_id=${id}`, { headers: authHeaders() })
              .then(res => res.json())
              .then(data => {
                if (data.status_code === 200 && data.data) {
                  try {
                    const info = typeof data.data.info === 'string' ? JSON.parse(data.data.info) : data.data.info;
                    if (info && info.hospital_id) {
                      loadPatients(info.hospital_id, id);
                      loadAppointments(id);
                    }
                  } catch (e) {
                    console.error('Error parsing user info:', e);
                  }
                }
              })
              .catch(err => console.error('Error fetching user details:', err));
          }
        }}
      />

      {showVideoCall && (
        <VideoCallModal
          isOpen={showVideoCall}
          onClose={() => setShowVideoCall(false)}
          roomId={`hospital_${doctorId}_${Date.now()}`}
        />
      )}

      {showSessionHistory && selectedPatient && (
        <div className="modal-overlay" onClick={() => {
          setShowSessionHistory(false);
          setSelectedPatient(null);
          setSelectedSessionDetails(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h2>{t('Session History')} - {selectedPatient.name}</h2>
            {sessionHistory.length === 0 ? (
              <p style={{ color: '#000000' }}>No session history available.</p>
            ) : (
              <div>
                <table className="session-history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Duration</th>
                      <th>Emotions</th>
                      <th>Assessment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessionHistory.map((session) => (
                      <tr key={session.session_id}>
                        <td>{formatDateFallback(session.session_date)}</td>
                        <td>{session.duration_seconds ? `${session.duration_seconds}s` : 'N/A'}</td>
                        <td>
                          {session.emotion ? (
                            <div style={{ fontSize: '12px' }}>
                              Joy: {session.emotion.joy?.toFixed(1)}%,
                              Stress: {session.emotion.stress?.toFixed(1)}%,
                              Sadness: {session.emotion.sadness?.toFixed(1)}%,
                              Anger: {session.emotion.anger?.toFixed(1)}%
                            </div>
                          ) : 'No data'}
                        </td>
                        <td>
                          <span className={`badge ${getAssessmentBadgeClass(session.emotion?.doctor_assessment)}`}>
                            {session.emotion?.doctor_assessment || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-secondary"
                            onClick={async () => {
                              setSelectedSessionDetails(session);
                              await loadSessionNotes(session.session_id);
                            }}
                          >
                            {t('View Details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: '16px' }}>
              <button className="btn" onClick={() => {
                setShowSessionHistory(false);
                setSelectedPatient(null);
                setSelectedSessionDetails(null);
              }}>{t('Close')}</button>
            </div>
          </div>
        </div>
      )}

      {selectedSessionDetails && (
        <div className="modal-overlay" onClick={() => {
          setSelectedSessionDetails(null);
          setSessionNotes([]);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>{t('Session Details')}</h2>
            <div className="session-details">
              <p><strong>Date:</strong> {formatDateFallback(selectedSessionDetails.session_date)}</p>
              <p><strong>Duration:</strong> {selectedSessionDetails.duration_seconds ? `${selectedSessionDetails.duration_seconds} seconds` : 'N/A'}</p>
              {selectedSessionDetails.emotion && (
                <div>
                  <p><strong>Emotion Analysis:</strong></p>
                  <ul>
                    <li>Joy: {selectedSessionDetails.emotion.joy?.toFixed(1)}%</li>
                    <li>Stress: {selectedSessionDetails.emotion.stress?.toFixed(1)}%</li>
                    <li>Sadness: {selectedSessionDetails.emotion.sadness?.toFixed(1)}%</li>
                    <li>Anger: {selectedSessionDetails.emotion.anger?.toFixed(1)}%</li>
                    <li>Burnout Score: {selectedSessionDetails.emotion.burnout_score?.toFixed(1)}%</li>
                  </ul>
                  <p><strong>Assessment:</strong>
                    <span className={`badge ${getAssessmentBadgeClass(selectedSessionDetails.emotion.doctor_assessment)}`}>
                      {selectedSessionDetails.emotion.doctor_assessment || 'N/A'}
                    </span>
                  </p>
                </div>
              )}
              <div className="notes-container">
                <p><strong>Session Notes:</strong></p>
                {sessionNotes.length === 0 ? (
                  <p style={{ fontStyle: 'italic', color: '#6b7280' }}>No notes available.</p>
                ) : (
                  <div>
                    {sessionNotes.map((note) => (
                      <div key={note.note_id} className="note-item">
                        <p className="note-date">
                          {formatDateFallback(note.created_at)}
                        </p>
                        <p className="note-text">{note.note_text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button className="btn" onClick={() => {
              setSelectedSessionDetails(null);
              setSessionNotes([]);
            }}>Close</button>
          </div>
        </div>
      )}

      <ReportView
        patient={reportPatient}
        sessionHistory={sessionHistory}
        lastSessionNote={lastSessionNote}
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        onViewHistory={(patient) => {
          setShowReportModal(false);
          handleViewHistory(patient);
        }}
      />

      {showDeleteAppointmentModal && selectedAppointmentForDelete && (
        <div className="modal-overlay" onClick={() => {
          setShowDeleteAppointmentModal(false);
          setSelectedAppointmentForDelete(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>Delete Appointment</h2>
            <p>Are you sure you want to delete this appointment?</p>
            <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
              <p><strong>Patient:</strong> {selectedAppointmentForDelete.patient_name || 'Unknown'}</p>
              <p><strong>Date & Time:</strong> {formatDateTimeFallback(selectedAppointmentForDelete.date, selectedAppointmentForDelete.time)}</p>
              <p><strong>Purpose:</strong> {selectedAppointmentForDelete.purpose || '-'}</p>
            </div>
            <p style={{ color: '#ef4444', fontSize: '14px' }}>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                className="btn"
                onClick={async () => {
                  if (!doctorId || !selectedAppointmentForDelete) return;
                  try {
                    const res = await doctorDeleteAppointment(selectedAppointmentForDelete.id, doctorId);
                    if (res.status_code === 200) {
                      alert('Appointment deleted successfully!');
                      setShowDeleteAppointmentModal(false);
                      setSelectedAppointmentForDelete(null);
                      loadAppointments(doctorId);
                    } else {
                      alert(res.message || 'Failed to delete appointment');
                    }
                  } catch (error) {
                    console.error('Error deleting appointment:', error);
                    alert('Error deleting appointment. Please try again.');
                  }
                }}
                style={{ background: '#ef4444', color: '#fff' }}
              >
                Delete
              </button>
              <button
                className="btn"
                onClick={() => {
                  setShowDeleteAppointmentModal(false);
                  setSelectedAppointmentForDelete(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
